"use client";

import React, { useMemo } from "react";
import { Separator } from "@/components/ui/separator";
import Marquee from "react-fast-marquee";
import { useTranslation } from "react-i18next";
import { formatTrade } from "@/helpers/helperFunctions";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { brokersList } from "../connection/BrokerList";
import { TrendingUp, Laptop, Zap, Radar, Workflow } from "lucide-react";

import { useStrategyMode } from "@/contexts/StrategyModeContext";

// Reusable Stat Row Component
const Stat = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: number | string;
  icon?: React.ReactNode;
}) => (
  <div className="flex gap-1.5 items-center not-italic transition-all duration-150 text-muted-foreground">
    {icon}
    <div className="text-sm font-semibold text-muted-foreground ">{label}</div>
    <div className="text-sm font-semibold text-[--accent-primary]">{value}</div>
  </div>
);

export const HeaderStats = ({ headerTrades }: { headerTrades: any }) => {
  const { t } = useTranslation("layout-page");
  const { details } = useAppDetails();
  const { mode } = useStrategyMode();
  const isPredictionMarket = mode === "prediction_market";

  const stats = useMemo(() => {
    if (isPredictionMarket) {
      return [
        {
          label: t("Strategies"),
          value: formatTrade(
            headerTrades?.deployed_counts_per_broker?.[27] ?? 0,
          ),
          icon: <Workflow className="w-3.5 h-3.5 " />,
        },
      ];
    }

    const baseStats = [
      {
        label: t("Strategies"),
        value: formatTrade(headerTrades?.total_strategies),
        icon: <Workflow className="w-3.5 h-3.5 " />,
      },
      {
        label: t("Trades"),
        value: formatTrade(headerTrades?.total_trades),
        icon: <TrendingUp className="w-3.5 h-3.5 " />,
      },
    ];

    if (details?.isWhiteLabled) {
      return [
        ...baseStats,
        {
          label: t("Live"),
          value: headerTrades?.deployed_counts_per_broker?.[details?.id] ?? 0,
          icon: <Zap className="w-3.5 h-3.5 " />,
        },
        {
          label: t("Scanners"),
          value: formatTrade(headerTrades?.scanners_deployed),
          icon: <Radar className="w-3.5 h-3.5 " />,
        },
      ];
    }

    return [
      ...baseStats,
      {
        label: t("Virtual"),
        value: brokersList?.reduce((sum, broker) => {
          if (broker?.type === "virtual") {
            return (
              sum +
              (headerTrades?.deployed_counts_per_broker?.[broker?.id] ?? 0)
            );
          }
          return sum;
        }, 0),
        icon: <Laptop className="w-3.5 h-3.5 " />,
      },
      {
        label: t("Live"),
        value: brokersList?.reduce((sum, broker) => {
          if (broker?.type === "live") {
            return (
              sum +
              (headerTrades?.deployed_counts_per_broker?.[broker?.id] ?? 0)
            );
          }
          return sum;
        }, 0),
        icon: <Zap className="w-3.5 h-3.5 " />,
      },
      {
        label: t("Scanners"),
        value: formatTrade(headerTrades?.scanners_deployed),
        icon: <Radar className="w-3.5 h-3.5 " />,
      },
    ];
  }, [details?.isWhiteLabled, headerTrades, t, isPredictionMarket]);

  return (
    <>
      {/* Desktop layout */}
      <div className="xl:flex hidden flex-wrap items-center gap-4 text-[--accent-primary]">
        {stats.map((item, i) => (
          <React.Fragment key={i}>
            <Stat label={item.label} value={item.value} icon={item.icon} />
            <Separator orientation="vertical"></Separator>
          </React.Fragment>
        ))}
      </div>

      {/* Mobile marquee */}
      <div className="xl:hidden flex max-w-screen w-full grow p-2">
        <Marquee className="max-w-screen">
          <ul className="relative z-10 flex flex-row gap-4 text-sm mr-3">
            {stats.map((item, i) => (
              <li
                key={i}
                className="flex gap-2 items-center min-w-[7rem] text-muted-foreground"
              >
                {item.icon}
                <div className="text-xs text-muted-foreground ">
                  {item.label}
                </div>
                <div className="text-xs font-semibold text-[--accent-primary] ">
                  {item.value}
                </div>
              </li>
            ))}
          </ul>
        </Marquee>
      </div>
    </>
  );
};

export default HeaderStats;
